import { Button, Modal, ModalHandle, ModalProps } from '@lfg/ui/components';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Grid2, Stack, Typography } from '@mui/material';
import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';

import { REVERSE_PROXY_IP } from 'src/global/config/common.config';
import { VerifyDNSType } from 'src/global/schemas/project.schema';
import { colors } from 'src/global/styles/colors';

export interface VerifyDNSModalHandle extends ModalHandle {}

export interface VerifyDNSModalProps extends Omit<ModalProps, 'children'> {
  onSubmit: () => void;
  loading?: boolean;
  verifyDomainData?: VerifyDNSType;
  isVerified?: boolean;
  isDnsVerified?: boolean;
}
export const VerifyDNSModal = forwardRef<VerifyDNSModalHandle, VerifyDNSModalProps>(
  ({ onSubmit, loading, verifyDomainData, isVerified, isDnsVerified, ...rest }, ref) => {
    const modalRef = useRef<ModalHandle>(null);

    const onShow = useCallback(() => modalRef.current?.show(), [modalRef.current]);
    const onHide = useCallback(() => modalRef.current?.hide(), [modalRef.current]);

    useImperativeHandle(ref, () => ({ show: onShow, hide: onHide }), [onShow, onHide]);

    return (
      <Modal
        ref={modalRef}
        bgColor={colors.modal}
        sx={{
          maxWidth: '560px',
          width: '100%',
          boxShadow: '0px 6px 10px 4px rgba(0, 0, 0, 0.15), 0px 2px 3px 0px rgba(0, 0, 0, 0.30)',
          border: `1px solid ${colors.neutral015}`,
        }}
        {...rest}>
        <Stack gap={3} flexGrow={1}>
          <Stack gap={2} alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
            <Typography component={'h3'} fontSize={'18px'} fontWeight={'500'} color={'white'}>
              Domain not verified
            </Typography>
            <CloseIcon onClick={onHide} sx={{ color: 'white', cursor: 'pointer' }} />
          </Stack>
          <Box>
            <Typography color='white' className='text-body_m font-gotham' marginBottom={'16px'}>
              Please recheck your DNS settings for and try again.
            </Typography>
            <Typography color='white' className='text-body_m font-gotham '>
              Ensure you have added:
            </Typography>
            <Grid2 container spacing={2} paddingTop={2}>
              <Grid2 size={2}>
                <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                  Type
                </Typography>
                {!isDnsVerified ? (
                  <Typography paddingBottom={1} color={'white'} fontSize={'14px'} variant='h6'>
                    CNAME
                  </Typography>
                ) : null}
                {verifyDomainData?.verification?.[0]?.type && !isVerified && (
                  <Typography paddingBottom={1} color={'white'} fontSize={'14px'} variant='h6'>
                    {verifyDomainData?.verification?.[0]?.type}
                  </Typography>
                )}
              </Grid2>
              <Grid2 size={8}>
                <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
                  Value
                </Typography>
                {!isDnsVerified ? (
                  <Typography paddingBottom={1} color={'white'} fontSize={'14px'} variant='h6'>
                    {REVERSE_PROXY_IP}
                  </Typography>
                ) : null}
                {verifyDomainData?.verification?.[0]?.value && !isVerified && (
                  <Typography paddingBottom={1} color={'white'} fontSize={'14px'} variant='h6'>
                    {verifyDomainData?.verification?.[0]?.value}
                  </Typography>
                )}
              </Grid2>
            </Grid2>
          </Box>
          <Stack direction={'row'} justifyContent={'end'} alignItems={'center'} spacing={2}>
            <Button
              onClick={onHide}
              sx={{
                padding: '14px 32px',
                borderRadius: '4px !important',
                backgroundColor: `${colors.neutral06} !important`,
                color: 'white',
                display: 'flex',
                justifyContent: 'center',
                gap: '8px',
                alignItems: 'center',
                height: '40px !important',
              }}>
              Cancel
            </Button>
            <Button
              isLoading={loading}
              onClick={onSubmit}
              bgColor={'#F04923'}
              borderRadius={'4px'}
              sx={{
                width: '140px !important',
                padding: '14px 32px',
                borderRadius: '4px !important',
                color: 'white',
                display: 'flex',
                justifyContent: 'center',
                gap: '8px',
                alignItems: 'center',
                height: '40px !important',
              }}>
              Verify
            </Button>
          </Stack>
        </Stack>
      </Modal>
    );
  },
);
